// src/components/onboarding/onboardingChrome.ts
// ─────────────────────────────────────────────────────────────────────────────
// Shared chrome for the two first-run surfaces: the WELCOME SETUP MODAL (shown
// once, on first login) and the TUTORIAL OVERLAY (launched on demand from the
// dashboard). They are deliberately independent flows, but they are the same
// product moment visually, so the card and the stacking order live here rather
// than being duplicated — or, worse, quietly diverging.
// ─────────────────────────────────────────────────────────────────────────────

import { alpha } from "@mui/material";
import type { SxProps, Theme } from "@mui/material";
import { athenaColors } from "../../theme/palette";

// ── Stacking ───────────────────────────────────────────────────────────────
// These surfaces sit above the entire app, which also puts them far above MUI's
// default modal layer (theme.zIndex.modal === 1300). Consequence: anything
// PORTALLED out of a card (a Select menu, a Popover, a Tooltip) lands at 1300
// and is painted BEHIND the backdrop — the control opens into an invisible
// layer and reads as broken. Portalled children MUST be lifted to Z_POPOVER.
export const Z_OVERLAY = 9999;
export const Z_CUTOUT = 10000;
export const Z_CARD = 10001;
export const Z_POPOVER = 10002;

/** Dim backdrop used by both surfaces. */
export const onboardingBackdropSx = (
  centered: boolean
): SxProps<Theme> => ({
  position: "fixed",
  inset: 0,
  zIndex: Z_OVERLAY,
  ...(centered
    ? {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: alpha(athenaColors.deepNavy, 0.55),
        backdropFilter: "blur(2px)",
      }
    : {}),
});

/**
 * The frosted navy card. `centered` drives the fixed-centre transform (welcome
 * modal, congrats, processing) versus absolute placement next to a spotlight.
 */
export const onboardingCardSx = (centered: boolean) => ({
  background: `linear-gradient(160deg, ${athenaColors.deepNavy} 0%, ${alpha(
    athenaColors.royalBlue,
    0.95
  )} 100%)`,
  backdropFilter: "blur(16px)",
  borderRadius: 3,
  border: `1px solid ${alpha(athenaColors.periwinkle, 0.3)}`,
  boxShadow: `0 24px 80px ${alpha(
    athenaColors.deepNavy,
    0.5
  )}, 0 0 30px ${alpha(athenaColors.royalBlue, 0.2)}`,
  p: 3,
  animation: "tutorialFadeIn 0.3s ease-out",
  "@keyframes tutorialFadeIn": {
    from: {
      opacity: 0,
      transform: centered ? "translate(-50%, -48%)" : "translateY(8px)",
    },
    to: {
      opacity: 1,
      transform: centered ? "translate(-50%, -50%)" : "translateY(0)",
    },
  },
});

/**
 * Field styling for controls rendered on the card. Kept here because the
 * DISABLED rules are subtle: the white ink needed on navy is inherited by a
 * disabled control, so without an explicit override a dead input looks live.
 */
export const onboardingFieldSx = {
  "& .MuiFormControlLabel-label": {
    color: athenaColors.white,
    fontWeight: 600,
  },
  "& .MuiOutlinedInput-root": {
    backgroundColor: alpha(athenaColors.white, 0.07),
    color: athenaColors.white,
    "& fieldset": { borderColor: alpha(athenaColors.periwinkle, 0.3) },
    "&:hover fieldset": { borderColor: alpha(athenaColors.periwinkle, 0.55) },
    "&.Mui-focused fieldset": { borderColor: athenaColors.periwinkle },
  },
  "& .MuiSelect-select": { color: athenaColors.white },
  "& .MuiSelect-icon": { color: alpha(athenaColors.white, 0.6) },
  // Two class selectors beat the single-class rules above, and this block is
  // last, so it also wins over ":hover fieldset" at equal specificity.
  "& .MuiOutlinedInput-root.Mui-disabled": {
    backgroundColor: alpha(athenaColors.white, 0.03),
    "& fieldset": { borderColor: alpha(athenaColors.white, 0.12) },
    "& .MuiSelect-select": {
      WebkitTextFillColor: alpha(athenaColors.white, 0.38),
    },
    "& .MuiSelect-icon": { color: alpha(athenaColors.white, 0.25) },
  },
  "& .MuiSwitch-track": {
    backgroundColor: alpha(athenaColors.white, 0.2),
  },
  "& .Mui-checked + .MuiSwitch-track": {
    backgroundColor: alpha(athenaColors.periwinkle, 0.5),
  },
} as const;
